import {
  AbortError,
  isAbortOrCanceledError,
  mergeAbortSignals,
} from "@mirage-web/utils/abort-signals";
import { mergeCallbackOptions } from "@mirage-web/utils/merge-callbacks";
import { promiseWithResolvers } from "@mirage-web/utils/promise-with-resolvers";
import { useOnUnmountEffect, usePromiseResolver } from "@mirage-web/utils/react-helpers";
import { sleep } from "@mirage-web/utils/sleep";
import { DefaultError, useMutation, UseMutationOptions } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { z } from "zod";

import { ApiHost } from "./api-client";
import { NetworkingLayerConfig } from "./config";
import {
  DefaultMutationFetcherParams,
  extractVariables,
  makeDefaultMutationFetcher,
  makeDefaultPollingFetcher,
  PollingFetcherParamsWithVariables,
} from "./default-fetcher";
import { CallerCombinedVariables } from "./definitions/types";
import { KnownApiError } from "./known-api-error";
import { NetworkAnalyticsOptions, QueryMethod } from "./requester";

export const POLLING_TIMING_OPTIONS = {
  default: {
    // total of 300 * 1000 = 300 seconds = 5 minutes
    maxPollingAttempts: 300,
    pollingInterval: 1000,
  },
  long: {
    // total of 1000 * 3600 = 3600 seconds = 1 hour
    maxPollingAttempts: 1000,
    pollingInterval: 3600,
  },
} as const;

export class PollingFailedError<FailedResult> extends Error {
  name = "PollingFailedError";

  constructor(public result: FailedResult) {
    super("Polling failed");
  }
}

export class PollingMaxAttemptsExceededError<ResponseSchema extends z.ZodTypeAny> extends Error {
  name = "PollingMaxAttemptsExceededError";

  constructor(public lastResponse: z.infer<ResponseSchema>) {
    super("Polling max attempts exceeded");
  }
}

interface PollingResultDone<DoneResult> {
  state: "done";
  result: DoneResult;
}

interface PollingResultPending<PendingResult> {
  state: "pending";
  result: PendingResult;
}

interface PollingResultFailed<FailedResult> {
  state: "failed";
  result: FailedResult;
}

export type RetryResult = { type: "retry"; delay: number } | { type: "giveup" };

export type PollingResult<DoneResult, PendingResult, FailedResult> =
  | PollingResultDone<DoneResult>
  | PollingResultPending<PendingResult>
  | PollingResultFailed<FailedResult>;

export interface PollingDetermineResultCreator {
  done: <DoneResult>(result: DoneResult) => PollingResultDone<DoneResult>;
  pending: <PendingResult = null>(result?: PendingResult) => PollingResultPending<PendingResult>;
  failed: <FailedResult = null>(result?: FailedResult) => PollingResultFailed<FailedResult>;
}

// this is a slightly weird pattern, but it's the only way i can get the type inference to work
const resultCreator = (): PollingDetermineResultCreator => ({
  done: <DR>(result: DR): PollingResultDone<DR> => ({
    state: "done",
    result,
  }),
  // oxlint-disable-next-line typescript/no-explicit-any
  pending: <PR = null>(result: PR = null as any): PollingResultPending<PR> => ({
    state: "pending",
    result,
  }),
  // oxlint-disable-next-line typescript/no-explicit-any
  failed: <FR = null>(result: FR = null as any): PollingResultFailed<FR> => ({
    state: "failed",
    result,
  }),
});

export interface BaseCreateHookOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
> {
  host: ApiHost;
  method: QueryMethod;
  path: Path;
  paramsSchema?: ParamsSchema;
  responseSchema: ResponseSchema;

  headers?: Record<string, string>;

  analytics?: NetworkAnalyticsOptions<ParamsSchema, ResponseSchema>;
}

interface CustomUsePollingCallerOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  DoneResult,
  PendingResult,
  FailedResult,
> extends Omit<
  UseMutationOptions<z.infer<ResponseSchema>, DefaultError, z.infer<ParamsSchema>, unknown>,
  "mutationFn" | "onError" | "onSuccess"
> {
  /**
   * returning promise allows you to keep `isPending` true while you're invalidating another query
   */
  onSuccess?: (
    data: DoneResult,
    variables: CallerCombinedVariables<Path, ParamsSchema>
  ) => void | Promise<void>;

  onPollingPendingResult?: (
    result: PendingResult,
    variables: CallerCombinedVariables<Path, ParamsSchema>
  ) => void;

  onError?: (
    error:
      | Error
      | PollingFailedError<FailedResult>
      | PollingMaxAttemptsExceededError<ResponseSchema>,
    variables: CallerCombinedVariables<Path, ParamsSchema>
  ) => void;

  swallowAbortErrors?: boolean;
}

const defaultDetermineRetry = (err: unknown): RetryResult => {
  if (err instanceof z.ZodError) {
    return { type: "giveup" };
  }

  // if the request was cancelled, we don't want to retry
  if (isAbortOrCanceledError(err)) {
    return { type: "giveup" };
  }

  // 4xx responses won't change by retrying — give up immediately
  if (err instanceof KnownApiError && err.responseStatus >= 400 && err.responseStatus < 500) {
    return { type: "giveup" };
  }

  // for now, we are always going to retry
  return { type: "retry", delay: 5000 };
};

interface CreateUsePollingHookOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  DoneResult,
  PendingResult,
  FailedResult,
  TransformedResult extends object,
>
  extends
    BaseCreateHookOptions<Path, ParamsSchema, ResponseSchema>,
    CustomUsePollingCallerOptions<
      Path,
      ParamsSchema,
      ResponseSchema,
      DoneResult,
      PendingResult,
      FailedResult
    > {
  maxPollingAttempts?: number;
  pollingInterval?: number;

  determinePollingStatus: (
    status: TransformedResult,
    resultCreator: PollingDetermineResultCreator
  ) => PollingResult<DoneResult, PendingResult, FailedResult>;
  determineRetry?: (err: unknown) => RetryResult;

  transformer?: (response: z.infer<ResponseSchema>) => TransformedResult;
}

export function createUsePollingHook<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  DoneResult,
  PendingResult,
  FailedResult,
  TransformedResult extends object = z.infer<ResponseSchema>,
>(
  config: NetworkingLayerConfig,
  {
    path,
    method,
    responseSchema,
    headers,
    paramsSchema,
    ...definitionOptions
  }: CreateUsePollingHookOptions<
    Path,
    ParamsSchema,
    ResponseSchema,
    DoneResult,
    PendingResult,
    FailedResult,
    TransformedResult
  >
) {
  const {
    host,
    analytics,
    transformer,
    determinePollingStatus,
    determineRetry = defaultDetermineRetry,
    maxPollingAttempts = POLLING_TIMING_OPTIONS.default.maxPollingAttempts,
    pollingInterval = POLLING_TIMING_OPTIONS.default.pollingInterval,
    ...defaultOptions
  } = definitionOptions;

  return function useCustomPollingHook(
    options: CustomUsePollingCallerOptions<
      Path,
      ParamsSchema,
      ResponseSchema,
      DoneResult,
      PendingResult,
      FailedResult
    > = {}
  ) {
    // TODO(jason): follow up - automatically start polling if params are provided in caller options

    type TypedBegin =
      CallerCombinedVariables<Path, ParamsSchema> extends Record<string, never>
        ? () => void
        : (variables: CallerCombinedVariables<Path, ParamsSchema>) => void;
    type TypedBeginAsync =
      CallerCombinedVariables<Path, ParamsSchema> extends Record<string, never>
        ? () => Promise<DoneResult>
        : (variables: CallerCombinedVariables<Path, ParamsSchema>) => Promise<DoneResult>;

    const { resolve, reject, reset: resetPromise } = usePromiseResolver<DoneResult>();
    const attemptCountRef = useRef(0);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const abortControllerRef = useRef<AbortController>(new AbortController());

    const [overriddenError, setOverriddenError] = useState<Error | undefined>();
    const [currentResult, setCurrentResult] =
      useState<PollingResult<DoneResult, PendingResult, FailedResult>>();

    const { onError, onSuccess, onPollingPendingResult, ...mergedOptions } = mergeCallbackOptions(
      ["onSuccess", "onError", "onPollingPendingResult"],
      defaultOptions,
      options
    );

    const swallowAbortErrors =
      options?.swallowAbortErrors ?? definitionOptions.swallowAbortErrors ?? false;

    const handleError = useCallback(
      (error: Error, variables: CallerCombinedVariables<Path, ParamsSchema>) => {
        if (swallowAbortErrors && isAbortOrCanceledError(error)) {
          return;
        }

        reject(error);

        onError?.(error, variables);
        setOverriddenError(error);

        if (error instanceof PollingFailedError) {
          setCurrentResult({ state: "failed", result: error.result });
        } else {
          setCurrentResult(undefined);
        }

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      },
      [reject, onError, swallowAbortErrors]
    );

    const {
      mutate: originalMutate,
      mutateAsync: originalMutateAsync,
      isPending,
      error,
      reset: resetMutation,
    } = useMutation({
      mutationFn: makeDefaultPollingFetcher(config, {
        path: path as Path,
        method,
        paramsSchema,
        responseSchema,
        host,
        headers,
        analytics,
        getSignal: () => abortControllerRef.current.signal,
        transformer,
      }),
      ...mergedOptions,

      onSuccess(data, paramsWithVariables) {
        const status = determinePollingStatus(data, resultCreator());
        setCurrentResult(status);

        if (status.state === "done") {
          resolve(status.result);

          // this one we return to allow chaining onSuccess promise callbacks
          return onSuccess?.(status.result, paramsWithVariables.variables);
        } else if (status.state === "pending") {
          onPollingPendingResult?.(status.result as PendingResult, paramsWithVariables.variables);

          scheduleNextMutateIfPossible(data, paramsWithVariables);
        } else {
          handleError(new PollingFailedError(status.result), paramsWithVariables.variables);
        }
      },

      async onError(error, paramsWithVariables) {
        if (determineRetry) {
          const retry = determineRetry(error);
          if (retry.type === "giveup") {
            handleError(error, paramsWithVariables.variables);
          } else {
            attemptCountRef.current++;

            if (attemptCountRef.current > maxPollingAttempts) {
              handleError(
                new PollingMaxAttemptsExceededError(error),
                paramsWithVariables.variables
              );

              return;
            }

            await sleep(retry.delay);
            originalMutate(paramsWithVariables);
          }
        } else {
          handleError(error, paramsWithVariables.variables);
        }
      },
    });

    const reset = useCallback(
      (p?: { requestWasCancelled?: boolean }) => {
        attemptCountRef.current = 1;
        setOverriddenError(undefined);
        setCurrentResult(undefined);

        if (!p?.requestWasCancelled) {
          // do not reset the abort controller if the request was cancelled
          // since that will mean when we check for aborted it will be false
          // since it's a new instance.
          // we will call reset again before next mutation to reset the abort controller
          abortControllerRef.current = new AbortController();
        }

        resetMutation();
        return resetPromise();
      },
      [resetPromise, resetMutation]
    );

    const scheduleNextMutateIfPossible = useCallback(
      (
        lastResponse: z.infer<ResponseSchema>,
        paramsWithVariables: PollingFetcherParamsWithVariables<Path, ParamsSchema>
      ) => {
        if (attemptCountRef.current > maxPollingAttempts) {
          handleError(
            new PollingMaxAttemptsExceededError(lastResponse),
            paramsWithVariables.variables
          );

          return;
        }

        if (paramsWithVariables.signal.aborted) {
          handleError(new AbortError(), paramsWithVariables.variables);

          return;
        }

        attemptCountRef.current++;

        timeoutRef.current = setTimeout(() => {
          originalMutate(paramsWithVariables);
        }, pollingInterval);
      },
      [originalMutate, handleError]
    );

    const begin = useCallback(
      (variables: CallerCombinedVariables<Path, ParamsSchema>) => {
        // silence uncaught promise errors
        reset().promise.catch(() => {});

        originalMutate({ variables, signal: abortControllerRef.current.signal });
      },
      [originalMutate, reset]
    ) as TypedBegin;

    const beginAsync = useCallback(
      (variables: CallerCombinedVariables<Path, ParamsSchema>) => {
        const newPromise = reset();

        originalMutateAsync({ variables, signal: abortControllerRef.current.signal }).catch((e) => {
          if (!isAbortOrCanceledError(e)) {
            console.error("caught error in beginAsync", e);
          }
          newPromise.reject(e);
        });

        return newPromise.promise;
      },
      [originalMutateAsync, reset]
    ) as TypedBeginAsync;

    const cancel = useCallback(() => {
      abortControllerRef.current.abort();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      reset({ requestWasCancelled: true });
    }, [reset]);

    useOnUnmountEffect(() => {
      cancel();
    });

    return {
      cancel,
      begin,
      beginAsync,
      isPending: isPending || currentResult?.state === "pending",
      currentResult,
      error: overriddenError ?? error,
    };
  };
}

interface PollingRequesterCallerOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  DoneResult,
  PendingResult,
  FailedResult,
> {
  headers?: Record<string, string>;
  signal?: AbortSignal;

  onSuccess?: (data: DoneResult, variables: CallerCombinedVariables<Path, ParamsSchema>) => void;

  onPollingPendingResult?: (
    result: PendingResult,
    variables: CallerCombinedVariables<Path, ParamsSchema>
  ) => void;

  onError?: (
    error:
      | Error
      | PollingFailedError<FailedResult>
      | PollingMaxAttemptsExceededError<ResponseSchema>,
    variables: CallerCombinedVariables<Path, ParamsSchema>
  ) => void;

  maxPollingAttempts?: number;
  pollingInterval?: number;

  swallowAbortErrors?: boolean;
}

export interface PollingRequesterOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  DoneResult,
  PendingResult,
  FailedResult,
>
  extends
    Omit<
      PollingRequesterCallerOptions<
        Path,
        ParamsSchema,
        ResponseSchema,
        DoneResult,
        PendingResult,
        FailedResult
      >,
      "signal"
    >,
    Pick<
      DefaultMutationFetcherParams<Path, ParamsSchema, ResponseSchema, z.infer<ResponseSchema>>,
      "path" | "method" | "paramsSchema" | "responseSchema" | "analytics" | "host"
    > {
  determinePollingStatus: (
    status: z.infer<ResponseSchema>,
    resultCreator: PollingDetermineResultCreator
  ) => PollingResult<DoneResult, PendingResult, FailedResult>;
  determineRetry?: (err: unknown) => RetryResult;
}

// NOTE: this is working now but not used quite yet
// i want to re-orient some of the query stuff generally to be able to be used directly
// and with hooks.
export function createPollingRequester<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  DoneResult,
  PendingResult,
  FailedResult,
>(
  config: NetworkingLayerConfig,
  options: PollingRequesterOptions<
    Path,
    ParamsSchema,
    ResponseSchema,
    DoneResult,
    PendingResult,
    FailedResult
  >
) {
  type PollingRequesterCallerOptionsTyped = PollingRequesterCallerOptions<
    Path,
    ParamsSchema,
    ResponseSchema,
    DoneResult,
    PendingResult,
    FailedResult
  > &
    CallerCombinedVariables<Path, ParamsSchema>;

  const {
    determinePollingStatus,
    determineRetry = defaultDetermineRetry,
    maxPollingAttempts: originalMaxPollingAttempts = POLLING_TIMING_OPTIONS.default
      .maxPollingAttempts,
    pollingInterval: originalPollingInterval = POLLING_TIMING_OPTIONS.default.pollingInterval,

    ...definitionOptions
  } = options;

  const trigger = (options: PollingRequesterCallerOptionsTyped) => {
    const { resolve, reject, promise } = promiseWithResolvers<DoneResult>();

    const {
      onSuccess,
      onPollingPendingResult,
      onError,
      signal,
      maxPollingAttempts: overriddenMaxPollingAttempts,
      pollingInterval: overriddenPollingInterval,
      ...requestOptions
    } = mergeCallbackOptions(
      ["onSuccess", "onPollingPendingResult", "onError"],
      options,
      definitionOptions as unknown as PollingRequesterCallerOptionsTyped
    );

    const swallowAbortErrors =
      requestOptions.swallowAbortErrors ?? definitionOptions.swallowAbortErrors ?? false;

    const abortController = new AbortController();
    const combinedSignal = mergeAbortSignals(signal, abortController.signal);

    const handleError = (error: Error) => {
      if (swallowAbortErrors && isAbortOrCanceledError(error)) {
        return;
      }

      onError?.(error, extractVariables(requestOptions));
      reject(error);
    };

    const { onError: _, onSuccess: __, ...restDefinitionOptions } = definitionOptions;

    const fetcher = makeDefaultMutationFetcher(config, {
      ...restDefinitionOptions,
      ...requestOptions,
      getSignal: () => combinedSignal,
    });

    const maxPollingAttempts = overriddenMaxPollingAttempts ?? originalMaxPollingAttempts;
    const pollingInterval = overriddenPollingInterval ?? originalPollingInterval;
    let lastResponse: z.infer<ResponseSchema>;

    const poll = async (attempt: number = 1) => {
      if (attempt > maxPollingAttempts) {
        handleError(new PollingMaxAttemptsExceededError(lastResponse));
        return;
      }

      try {
        combinedSignal?.throwIfAborted();

        lastResponse = await fetcher(extractVariables(requestOptions));
        const status = determinePollingStatus(lastResponse, resultCreator());

        if (status.state === "done") {
          onSuccess?.(status.result, extractVariables(requestOptions));
          resolve(status.result);
        } else if (status.state === "pending") {
          onPollingPendingResult?.(
            status.result as PendingResult,
            extractVariables(requestOptions)
          );

          setTimeout(() => poll(attempt + 1), pollingInterval);
        } else {
          handleError(new PollingFailedError(status.result));
        }
      } catch (error) {
        if (determineRetry) {
          const retry = determineRetry(error);
          if (retry.type === "giveup") {
            handleError(error as Error);
          } else {
            await sleep(retry.delay);
            poll(attempt + 1);
          }
        } else {
          handleError(error as Error);
        }
      }
    };

    poll();

    return {
      promise,
      cancel: () => {
        abortController.abort();
      },
    };
  };

  return trigger;
}
